// galaxy/sky.js: the real sky seen from the Sun.
//  - Pano: ESO/S. Brunier star-free Milky Way panorama (galactic plate carree)
//    on a sphere at infinity, drawn only while the camera is near the Sun.
//  - Near: the HYG naked-eye stars at their real 3D positions around the Sun
//    (plus the Sun itself), so constellations dissolve in parallax as we rise.
import * as THREE from 'three';
import { FullScreenQuad, GLSL_BLACKBODY, loadBinary } from '../lib/common.js';
import { SUN } from './model.js';

const PANO_FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
uniform sampler2D uPan;
uniform mat4 uInvProj, uCamWorld;
uniform float uGain, uPixAng, uTexW, uChK, uMagK, uLiftK;
uniform vec3 uWB, uLift;
void main() {
  vec4 v = uInvProj * vec4(vUv * 2.0 - 1.0, -1.0, 1.0);
  vec3 d = normalize((uCamWorld * vec4(normalize(v.xyz / v.w), 0.0)).xyz);
  // world -> galactic: X_gal = -z (to the centre), Y_gal = -x (l = 90), Z_gal = +y (NGP)
  float l = atan(-d.x, -d.z), b = asin(clamp(d.y, -1.0, 1.0));
  vec2 uv = vec2(0.5 - l / 6.2831853, 0.5 + b / 3.1415927);
  float lod = clamp(log2(uPixAng * uTexW / 6.2831853), 0.0, 8.0);
  vec3 c = textureLod(uPan, uv, lod).rgb;
  // chroma from a softer mip level: hides the JPEG's blocky chroma, the luminance keeps full detail
  vec3 cs = textureLod(uPan, uv, lod + 1.5).rgb;
  const vec3 LUM = vec3(0.2126, 0.7152, 0.0722);
  float ly = dot(c, LUM), ls = dot(cs, LUM);
  // SPECTRUM grade r7 (representative colour): the photo's own colour, gently cooled. Luminance (star clouds,
  // the Great Rift, the bulge) is untouched. Chroma = the photo's, calmed (JPEG noise) and white-balanced to a
  // cooler white point: band mean rgb/ly (1.13, .97, .94) -> (.95, .96, 1.15). No luminance band maps to a hue.
  vec3 ch = cs / max(ls, 1e-4);
  ch = mix(vec3(1.0), ch, uChK * smoothstep(0.002, 0.02, ls)) * uWB;
  // a hint of magenta only where the photo's red channel stands out (HII)
  float hm = (max(cs.r - cs.g, 0.0) - 1.5 * max(cs.g - cs.b, 0.0)) / (ls + 0.01);
  ch = mix(ch, vec3(2.3, 0.55, 1.55), uMagK * smoothstep(0.1, 0.3, hm) * smoothstep(0.004, 0.02, ls));
  // a slight blue lift in the shadows
  ch = mix(ch, uLift, uLiftK * (1.0 - smoothstep(0.004, 0.04, ly)));
  ch /= max(dot(ch, LUM), 1e-3);
  c = ch * ly * (ly / (ly + 0.005)); // gentle toe: the faint sky sinks toward the void
  gl_FragColor = vec4(c * uGain, 1.0);
}`;

export class Pano {
  constructor() {
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uPan: { value: null }, uInvProj: { value: new THREE.Matrix4() }, uCamWorld: { value: new THREE.Matrix4() },
        uGain: { value: 1 }, uPixAng: { value: 0.001 }, uTexW: { value: 4096 },
        uChK: { value: 1 }, uMagK: { value: 0.7 }, uLiftK: { value: 0.35 }, uWB: { value: new THREE.Vector3(0.88, 1, 1.12) }, uLift: { value: new THREE.Vector3(0.62, 0.92, 1.8) },
      },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: PANO_FRAG,
      depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending,
    });
    this.q = new FullScreenQuad(this.mat);
    this.U = this.mat.uniforms;
  }
  setTexture(t) { this.U.uPan.value = t; this.U.uTexW.value = t.image ? t.image.width : 4096; }
  setCamera(camera, heightPx) {
    this.U.uInvProj.value.copy(camera.projectionMatrixInverse);
    this.U.uCamWorld.value.copy(camera.matrixWorld);
    this.U.uPixAng.value = (2 * Math.tan((camera.fov * Math.PI) / 360)) / Math.max(heightPx, 1);
  }
  render(renderer) { this.q.render(renderer); }
  dispose() { this.mat.dispose(); this.q.dispose(); }
}

const NEAR_VERT = /* glsl */`
${GLSL_BLACKBODY}
attribute vec2 aStar;          // (log10 L, B-V); L = flux (mag 0 = 1) at 1 kpc
uniform vec3 uCam;
uniform float uK, uPixSr, uSizeMin, uSizeMax, uFade, uEmax;
varying vec3 vCol;
void main() {
  vec3 d = position - uCam;
  float dd = max(dot(d, d), 1e-12);
  float E = uK * uFade * pow(10.0, aStar.x) / (dd * uPixSr);
  E = E / (1.0 + E / uEmax);
  if (E < 2e-4) { gl_Position = vec4(0.0, 0.0, -2.0, 1.0); gl_PointSize = 0.0; vCol = vec3(0.0); return; }
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  float sz = clamp(uSizeMin * (1.0 + 0.16 * log2(1.0 + E)), uSizeMin, uSizeMax);
  gl_PointSize = sz;
  float bv = aStar.y;
  float T = 4600.0 * (1.0 / (0.92 * bv + 1.7) + 1.0 / (0.92 * bv + 0.62));
  vCol = mix(vec3(1.0), blackbody(T), 0.75) * (E / (0.2488 * sz * sz));
}`;
const NEAR_FRAG = /* glsl */`
varying vec3 vCol;
void main() {
  vec2 q = gl_PointCoord * 2.0 - 1.0;
  float r2 = dot(q, q);
  if (r2 > 1.0) discard;
  gl_FragColor = vec4(vCol * exp(-3.0 * r2), 1.0);
}`;

// flux of a mag-0 star in the glow's units (intensity * sr), see world.js
export const F_MAG0 = 1.1e-4;

export async function makeNear(url) {
  const buf = new Int16Array(await loadBinary(url));
  const n = buf.length / 6;
  const pos = new Float32Array((n + 1) * 3), st = new Float32Array((n + 1) * 2);
  // the Sun: M_V = 4.83, B-V = 0.65
  pos.set([SUN.x, SUN.y, SUN.z], 0);
  st.set([-0.4 * 4.83 + 2 * Math.log10(0.01), 0.65], 0);
  for (let i = 0; i < n; i++) {
    const gx = buf[i * 6] / 32767, gy = buf[i * 6 + 1] / 32767, gz = buf[i * 6 + 2] / 32767;
    const dk = buf[i * 6 + 3] / 10 / 1000; // kpc
    const m = buf[i * 6 + 4] / 1000, bv = buf[i * 6 + 5] / 10000;
    const j = (i + 1) * 3;
    pos[j] = SUN.x - gy * dk; pos[j + 1] = SUN.y + gz * dk; pos[j + 2] = SUN.z - gx * dk;
    st[(i + 1) * 2] = -0.4 * m + 2 * Math.log10(Math.max(dk, 1e-6));
    st[(i + 1) * 2 + 1] = bv;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aStar', new THREE.BufferAttribute(st, 2));
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uCam: { value: new THREE.Vector3() }, uK: { value: F_MAG0 }, uPixSr: { value: 1e-6 },
      uSizeMin: { value: 2.8 }, uSizeMax: { value: 26 }, uFade: { value: 1 }, uEmax: { value: 500 },
    },
    vertexShader: NEAR_VERT, fragmentShader: NEAR_FRAG,
    transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  return { points, U: mat.uniforms, count: n + 1, dispose() { geo.dispose(); mat.dispose(); } };
}
